// C-4-1 거래처 배송정보 등록 — 화면 로직(순수 함수·상수). 컴포넌트·axios 의존 없음.
// 문구는 940 표현 우선, 940에 없는 문구는 이 파일 MSG 상수 한 곳에 모음(기획 확정 시 상수만 교체).
import {
  dateOnly,
  isValidPhone,
  normalizePhone,
  normalizeTime,
  nowTimeKst,
  todayKst,
  type ShareError,
} from '@indipia/shared-features';

// ───────────── 타입(B3·B4·B5·B6 응답 규격) ─────────────

export type DeliveryState = 'OPEN' | 'REPLIED' | 'RECEIVED' | 'CLOSED' | 'EXPIRED';
export type IssueYn = 'Y' | 'N';

export interface DeliveryPhoto {
  orderPhotoId: number;
  seq: number;
}

export interface DeliveryItem {
  itemName: string;
  itemSpec: string;
  itemQty: string;
  itemUnit: string;
}

export interface DeliveryInfo {
  arrivalDate: string; // YYYY-MM-DD
  arrivalTime: string; // HH:mm
  driverName: string;
  driverPhone: string; // 서버 저장값(숫자만 또는 하이픈 포함) — 표시는 formatSharePhone
  issueYn: IssueYn;
  issueNote: string;
  photoCnt: number;
  replyRound: number;
}

export interface DeliveryView {
  state: 'OPEN' | 'REPLIED' | 'RECEIVED';
  editable: boolean;
  companyName: string;
  /** 사업장 대표 연락처(만료 화면 문의처). 비어 있으면 줄 숨김 */
  companyPhone: string;
  siteLabel: string;
  workTypeName: string;
  vendorName: string;
  deliveryAddress: string;
  deliveryDate: string; // YYYY-MM-DD
  itemSummary: string;
  items: DeliveryItem[];
  delivery: DeliveryInfo | null;
  photos: DeliveryPhoto[];
}

export type ParsedDelivery =
  | { ok: true; view: DeliveryView }
  | { ok: false; screen: 'CLOSED' | 'EXPIRED' | 'UNKNOWN'; companyName?: string; companyPhone?: string; deliveryDate?: string };

/** B4·B5 응답 data */
export interface DeliverySubmitResult {
  state: string;
  replyRound: number;
  arrivalDate: string;
  arrivalTime: string;
  photoCnt: number;
  changed: boolean;
}

/** B4·B5 요청 본문(전체 교체 — 모든 필드를 항상 함께 보냄) */
export interface DeliveryPayload {
  arrivalDate: string;
  arrivalTime: string;
  driverName: string;
  driverPhone: string;
  issueYn: IssueYn;
  issueNote?: string;
}

export interface DeliveryFormValues {
  arrivalDate: string;
  arrivalTime: string;
  driverName: string;
  driverPhone: string;
  issueYn: IssueYn;
  issueNote: string;
}

export type DeliveryFieldKey =
  | 'arrivalDate'
  | 'arrivalTime'
  | 'driverName'
  | 'driverPhone'
  | 'issueYn'
  | 'issueNote';

export type DeliveryFieldErrors = Partial<Record<DeliveryFieldKey, string>>;

// ───────────── 상수 ─────────────

export const DRIVER_NAME_MAX = 50;
export const ISSUE_NOTE_MAX = 300;
export const PHOTO_MAX = 5;
/** 상차사진 1장 업로드 timeout(ms) — 초기값, 느린 회선 시험 후 조정(M10) */
export const PHOTO_UPLOAD_TIMEOUT_MS = 120000;

export const MSG = {
  title: '배송정보 등록',
  backToList: '← 목록으로',
  expiredTitle: '만료된 링크예요',
  expiredDescription: '입고일이 지나 더 이상 이용할 수 없어요. 문의는 요청한 곳으로 해 주세요.',
  inquiryLabel: '문의',
  documentTitle: '배송정보 등록',
  notEditable: '지금은 배송정보를 등록할 수 없는 상태예요. 요청한 곳으로 문의해 주세요.',
  repliedGuide: '도착 전까지 [수정하기]로 바꿀 수 있어요. 바꾸면 반장님께 다시 안내돼요.',
  received: '입고가 완료됐어요. 더 이상 등록하거나 고칠 수 없어요.',
  sentTitle: '배송정보를 보냈어요',
  sentDesc: '담당자와 반장님께 도착 시간이 안내돼요.',
  unchanged: '바뀐 내용이 없어서 그대로 두었어요.',
  alreadyReplied: '이미 보낸 배송정보가 있어서 불러왔어요. 고치려면 [수정하기]를 눌러 주세요.',
  deliveryNotFound: '아직 보낸 배송정보가 없어요. 내용을 확인하고 보내 주세요.',
  checkInput: '입력한 내용을 확인해 주세요.',
  submitFail: '배송정보를 보내지 못했어요. 네트워크를 확인하고 다시 눌러 주세요.',
  serverBusy: '잠시 후 다시 시도해 주세요.',
  arrivalPast: '이미 지난 시각이에요. 지금 이후 시각을 골라 주세요.',
  datePast: '오늘 이후 날짜를 골라 주세요.',
  timeStep: '도착 예정 시각은 30분 단위로 골라 주세요.',
  required: '필수 항목이에요.',
  phoneInvalid: '연락처를 확인해 주세요. (예: 010-0000-0000)',
  photoSizeOver: '사진은 한 장당 20MB까지 올릴 수 있어요.',
  photoTypeBad: 'JPG, PNG, WEBP, HEIC 사진만 올릴 수 있어요.',
  photoLimit: '상차사진은 최대 5장까지 올릴 수 있어요.',
  photoPayload: '사진 용량이 너무 커요. 더 작은 사진으로 올려 주세요.',
  photoFail: '사진을 올리지 못했어요. 다시 시도해 주세요.',
  photoDeleteFail: '사진을 삭제하지 못했어요. 다시 시도해 주세요.',
  btnRegister: '배송정보 보내기',
  btnEdit: '수정한 내용 보내기',
  btnMissing: '도착 예정시각 · 기사 정보를 입력해 주세요',
  btnIssueNote: '이슈 내용을 입력해 주세요',
  btnUploading: '사진을 올리는 중이에요',
  // 폼·요약·요청 카드 라벨 (컴포넌트에 직접 적지 않고 이 상수에서 사용)
  lateWarning: (deliveryDateLabel: string) =>
    `입고 희망일(${deliveryDateLabel})보다 늦어요. 그대로 보내면 입고 지연으로 표시돼요.`,
  labelArrivalDate: '도착 예정 날짜 (필수)',
  labelArrivalTime: '도착 예정 시각 (필수)',
  labelDriverName: '배송기사 이름 (필수)',
  labelDriverPhone: '배송기사 연락처 (필수)',
  driverPhonePlaceholder: '010-0000-0000',
  photoSectionTitle: (max: number) => `상차사진 (선택 · 최대 ${max}장)`,
  photoAdd: '사진 추가',
  photoFailedCount: (n: number) => `올리지 못한 사진이 ${n}장 있어요`,
  labelIssue: '배송 이슈 (필수)',
  issueNone: '이상 없음',
  issueYes: '이슈 있음',
  labelIssueNote: '이슈 내용 (필수)',
  issueNotePlaceholder: '예) 엘보 20개 중 15개만 상차, 나머지 10/10 오전',
  cancel: '취소',
  summaryArrival: '도착 예정',
  summaryDriver: '배송기사',
  summaryPhotos: '상차사진',
  summaryPhotosNone: '없음',
  summaryPhotoCount: (n: number) => `${n}장`,
  summaryPhotoOpen: (label: string) => `${label} 크게 보기`,
  summaryIssue: '배송 이슈',
  cardSite: '현장',
  cardWorkType: '공종',
  cardItems: '품목',
  cardDeliveryDate: '입고일',
  cardAddress: '배송지',
  btnStartEdit: '수정하기',
  viewerTitle: '상차사진',
} as const;

// ───────────── 서버 응답 정규화 ─────────────

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function parseDelivery(raw: unknown): DeliveryInfo {
  if (!raw || typeof raw !== 'object') throw new Error('delivery');
  const d = raw as Record<string, unknown>;
  const issueYn = d.issueYn;
  if (issueYn !== 'Y' && issueYn !== 'N') throw new Error('issueYn');
  if (typeof d.arrivalDate !== 'string' || typeof d.arrivalTime !== 'string') throw new Error('arrival');
  if (typeof d.photoCnt !== 'number' || typeof d.replyRound !== 'number') throw new Error('counts');
  return {
    arrivalDate: dateOnly(d.arrivalDate),
    arrivalTime: normalizeTime(d.arrivalTime),
    driverName: str(d.driverName),
    driverPhone: str(d.driverPhone),
    issueYn,
    issueNote: str(d.issueNote),
    photoCnt: d.photoCnt,
    replyRound: d.replyRound,
  };
}

function parsePhotos(raw: unknown): DeliveryPhoto[] {
  if (raw == null) return [];
  if (!Array.isArray(raw)) throw new Error('photos');
  return raw.map((p) => {
    const o = p as Record<string, unknown>;
    if (typeof o?.orderPhotoId !== 'number') throw new Error('photo');
    return { orderPhotoId: o.orderPhotoId, seq: typeof o.seq === 'number' ? o.seq : 0 };
  });
}

function parseItems(raw: unknown): DeliveryItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((it) => {
    const o = (it ?? {}) as Record<string, unknown>;
    return {
      itemName: str(o.itemName),
      itemSpec: str(o.itemSpec),
      itemQty: o.itemQty == null ? '' : String(o.itemQty),
      itemUnit: str(o.itemUnit),
    };
  });
}

function safeDate(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  try {
    return dateOnly(v);
  } catch {
    return undefined;
  }
}

/** 만료 화면 안내문 — 입고일을 알면 날짜를 넣는다(없으면 날짜 없는 기본 문구). */
export function expiredDescription(deliveryDate: string | undefined, formatDate: (d: string) => string): string {
  return deliveryDate
    ? `입고일(${formatDate(deliveryDate)})이 지나 더 이상 이용할 수 없어요. 문의는 요청한 곳으로 해 주세요.`
    : MSG.expiredDescription;
}

/**
 * B3 응답 → 화면 모델. 필수 신호(state·editable·deliveryDate, REPLIED의 delivery)가 없거나
 * 약속된 값 밖이면 폴백하지 않고 UNKNOWN. CLOSED·EXPIRED는 전체 화면용(데이터 없음).
 */
export function parseDeliveryView(raw: unknown): ParsedDelivery {
  if (!raw || typeof raw !== 'object') return { ok: false, screen: 'UNKNOWN' };
  const r = raw as Record<string, unknown>;
  const companyName = str(r.companyName) || undefined;
  if (r.state === 'CLOSED') return { ok: false, screen: 'CLOSED', companyName };
  if (r.state === 'EXPIRED') {
    // 만료 화면의 입고일·문의처 — 표시용이라 없거나 형식이 이상하면 해당 줄만 숨김
    return { ok: false, screen: 'EXPIRED', companyName, companyPhone: str(r.companyPhone) || undefined, deliveryDate: safeDate(r.deliveryDate) };
  }
  if (r.state !== 'OPEN' && r.state !== 'REPLIED' && r.state !== 'RECEIVED') {
    return { ok: false, screen: 'UNKNOWN', companyName };
  }
  try {
    const state = r.state;
    if (state !== 'RECEIVED' && typeof r.editable !== 'boolean') throw new Error('editable');
    if (typeof r.deliveryDate !== 'string') throw new Error('deliveryDate');
    const delivery = r.delivery == null ? null : parseDelivery(r.delivery);
    if (state === 'REPLIED' && !delivery) throw new Error('delivery');
    return {
      ok: true,
      view: {
        state,
        editable: state === 'RECEIVED' ? false : (r.editable as boolean),
        companyName: str(r.companyName),
        companyPhone: str(r.companyPhone),
        siteLabel: str(r.siteLabel),
        workTypeName: str(r.workTypeName),
        vendorName: str(r.vendorName),
        deliveryAddress: str(r.deliveryAddress),
        deliveryDate: dateOnly(r.deliveryDate),
        itemSummary: str(r.itemSummary),
        items: parseItems(r.items),
        delivery,
        photos: parsePhotos(r.photos),
      },
    };
  } catch {
    return { ok: false, screen: 'UNKNOWN', companyName };
  }
}

/** 품목 표시 문자열 — itemSummary 우선, 없으면 items 나열. 둘 다 없으면 빈 배열(줄 숨김). */
export function itemLines(view: Pick<DeliveryView, 'itemSummary' | 'items'>): string[] {
  if (view.itemSummary.trim()) return [view.itemSummary.trim()];
  return view.items
    .map((i) => [i.itemName, i.itemSpec, [i.itemQty, i.itemUnit].filter(Boolean).join('')].filter(Boolean).join(' '))
    .filter((s) => s.length > 0);
}

// ───────────── 폼 ─────────────

/** 미리 채움은 서버 값(B3 delivery 또는 수정 모드의 저장값)만. FE 기본값 없음(issueYn=N은 서버 기본값과 동일). */
export function initialFormValues(info: DeliveryInfo | null): DeliveryFormValues {
  if (!info) {
    return { arrivalDate: '', arrivalTime: '', driverName: '', driverPhone: '', issueYn: 'N', issueNote: '' };
  }
  return {
    arrivalDate: info.arrivalDate,
    arrivalTime: info.arrivalTime,
    driverName: info.driverName,
    driverPhone: info.driverPhone,
    issueYn: info.issueYn,
    issueNote: info.issueYn === 'Y' ? info.issueNote : '',
  };
}

const TIME_RE = /^([01]\d|2[0-3]):(00|30)$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** 서울 기준 오늘·현재. 서버가 최종 판정하므로 사전 안내용. */
export function validateDelivery(
  v: DeliveryFormValues,
  now: Date = new Date(),
): DeliveryFieldErrors {
  const err: DeliveryFieldErrors = {};
  const today = todayKst(now);

  if (!v.arrivalDate) err.arrivalDate = MSG.required;
  else if (!DATE_RE.test(v.arrivalDate) || v.arrivalDate < today) err.arrivalDate = MSG.datePast;

  if (!v.arrivalTime) err.arrivalTime = MSG.required;
  else if (!TIME_RE.test(v.arrivalTime)) err.arrivalTime = MSG.timeStep;
  else if (v.arrivalDate === today && v.arrivalTime <= nowTimeKst(now)) err.arrivalTime = MSG.arrivalPast;

  const name = v.driverName.trim();
  if (!name) err.driverName = MSG.required;
  else if (name.length > DRIVER_NAME_MAX) err.driverName = `이름은 ${DRIVER_NAME_MAX}자 이내로 입력해 주세요.`;

  if (!v.driverPhone.trim()) err.driverPhone = MSG.required;
  else if (!isValidPhone(v.driverPhone)) err.driverPhone = MSG.phoneInvalid;

  if (v.issueYn === 'Y') {
    const note = v.issueNote.trim();
    if (!note) err.issueNote = MSG.required;
    else if (note.length > ISSUE_NOTE_MAX) err.issueNote = `이슈 내용은 ${ISSUE_NOTE_MAX}자 이내로 입력해 주세요.`;
  }
  return err;
}

/** 제출 버튼 비활성 사유 라벨(필수 미입력 판정만). 없으면 null = 제출 가능 상태. */
export function submitBlockLabel(
  v: DeliveryFormValues,
  photoBusy: boolean,
): string | null {
  if (photoBusy) return MSG.btnUploading;
  if (!v.arrivalDate || !v.arrivalTime || !v.driverName.trim() || !v.driverPhone.trim()) return MSG.btnMissing;
  if (v.issueYn === 'Y' && !v.issueNote.trim()) return MSG.btnIssueNote;
  return null;
}

/** POST·PUT 공통 본문. 전화는 숫자만, issueNote는 issueYn=Y일 때만. */
export function buildDeliveryPayload(v: DeliveryFormValues): DeliveryPayload {
  const payload: DeliveryPayload = {
    arrivalDate: v.arrivalDate,
    arrivalTime: v.arrivalTime,
    driverName: v.driverName.trim(),
    driverPhone: normalizePhone(v.driverPhone),
    issueYn: v.issueYn,
  };
  if (v.issueYn === 'Y') payload.issueNote = v.issueNote.trim();
  return payload;
}

/** 도착일이 입고희망일보다 늦으면 경고(막지 않음, M6) */
export function isArrivalLate(arrivalDate: string, deliveryDate: string): boolean {
  return DATE_RE.test(arrivalDate) && DATE_RE.test(deliveryDate) && arrivalDate > deliveryDate;
}

/** 입력이 서버 저장값과 같은지(이탈 보호용 dirty 판정) */
export function isFormDirty(a: DeliveryFormValues, b: DeliveryFormValues): boolean {
  return (
    a.arrivalDate !== b.arrivalDate ||
    a.arrivalTime !== b.arrivalTime ||
    a.driverName !== b.driverName ||
    a.driverPhone !== b.driverPhone ||
    a.issueYn !== b.issueYn ||
    a.issueNote !== b.issueNote
  );
}

// ───────────── 오류 매핑 ─────────────

export type FatalScreen = 'SESSION' | 'LINK' | 'CLOSED' | 'EXPIRED' | 'CHANGED' | 'RECEIVED';

/** 서버 상태 오류 → 즉시 전환할 전체 화면. 해당 없으면 null. */
export function fatalScreenOf(e: ShareError): FatalScreen | null {
  switch (e.kind) {
    case 'SESSION':
    case 'LINK':
    case 'CLOSED':
    case 'EXPIRED':
    case 'CHANGED':
      return e.kind;
    case 'ALREADY_RECEIVED':
      return 'RECEIVED';
    default:
      return null;
  }
}

const FIELD_KEYS: readonly DeliveryFieldKey[] = [
  'arrivalDate',
  'arrivalTime',
  'driverName',
  'driverPhone',
  'issueYn',
  'issueNote',
];

/** VALIDATION_ERROR 400 + field → 필드 인라인. field 없음·모르는 값이면 null(폼 위 일반 안내). */
export function fieldErrorOf(e: ShareError): { field: DeliveryFieldKey; text: string } | null {
  const f = e.field as DeliveryFieldKey | undefined;
  if (!f || !FIELD_KEYS.includes(f)) return null;
  if (f === 'arrivalTime' && e.reason === 'ARRIVAL_TIME_PAST') return { field: f, text: MSG.arrivalPast };
  return { field: f, text: MSG.checkInput };
}

/** 상차사진 업로드 실패 코드 → 문구 */
export function photoFailText(code: string | undefined): string {
  switch (code) {
    case 'FILE_SIZE_EXCEEDED':
      return MSG.photoSizeOver;
    case 'FILE_TYPE_NOT_ALLOWED':
      return MSG.photoTypeBad;
    case 'ORDER_PHOTO_LIMIT':
      return MSG.photoLimit;
    case 'PAYLOAD_TOO_LARGE':
      return MSG.photoPayload;
    default:
      return MSG.photoFail;
  }
}

/** B6 응답(201, 일부 실패도 201) 해석: 성공 1장 or 실패 코드 */
export function interpretPhotoUpload(
  data: unknown,
): { ok: true; photo: DeliveryPhoto; photoCnt: number | null } | { ok: false; code?: string } {
  const d = (data ?? {}) as { photos?: unknown; failed?: unknown; photoCnt?: unknown };
  const failed = Array.isArray(d.failed) ? (d.failed as { code?: unknown }[]) : [];
  if (failed.length > 0) {
    const code = failed[0]?.code;
    return { ok: false, code: typeof code === 'string' ? code : undefined };
  }
  const photos = Array.isArray(d.photos) ? (d.photos as Record<string, unknown>[]) : [];
  const p = photos[0];
  if (!p || typeof p.orderPhotoId !== 'number') return { ok: false };
  return {
    ok: true,
    photo: { orderPhotoId: p.orderPhotoId, seq: typeof p.seq === 'number' ? p.seq : 0 },
    photoCnt: typeof d.photoCnt === 'number' ? d.photoCnt : null,
  };
}

/** 상차사진 라벨 — 표시 순서 기준 */
export function photoLabel(index: number): string {
  return `상차사진 ${index + 1}`;
}
